import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'components/Modal/Modal';
import ButtonCustom from 'components/ButtonCustom/ButtonCustom';
import { Grid, Box } from '@mui/material';
import showToast from "components/Toast/Toast";
import pencil from 'assets/icons/pencil.png'
import * as S from './style';
import { deleteCommunity, editCommunity } from 'utils/requests/communities';

const CommunityInfo = ({ data = {}, refetch }) => {
  const navigate = useNavigate();
  const [openConfirmDeletionModal, setOpenConfirmDeletionModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(data);

  useEffect(() => {
    setEditedData(data);
  }, [data]);

  const handleCommunityDelete = () => {
    setOpenConfirmDeletionModal(true);
  };

  const cancelCommunityDelete = () => {
    setOpenConfirmDeletionModal(false);
  };

  const handleDeleteCommunity = async (communityId) => {
    try {
      await deleteCommunity(communityId);
      showToast("Comunidade deletada com sucesso!", "success");
      setOpenConfirmDeletionModal(false);
      navigate('/comunidade');
    } catch (error) {
      showToast("Falha ao deletar comunidade", "error");
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData(data);
  };

  const handleSave = async () => {
    const body = {
      plan_details: JSON.stringify(editedData.plan_details),
    }
    try {
      await editCommunity(data.id, body);
      showToast("Comunidade atualizada com sucesso!", "success");
      setIsEditing(false);
      refetch();
    } catch (error) {
      showToast("Falha ao atualizar comunidade.", "error");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({
      ...prev,
      plan_details: {
        ...prev.plan_details,
        [name]: value
      }
    }));
  };

  const handleArrayChange = (name, value) => {
    setEditedData(prev => ({
      ...prev,
      plan_details: {
        ...prev.plan_details,
        [name]: value.split(',').map(item => item.trim())
      }
    }));
  }

  const handleRulesChange = (value) => {
    setEditedData(prev => ({
      ...prev,
      plan_details: {
        ...prev.plan_details,
        rules: value.split('\n').map(item => item.trim())
      }
    }));
  }

  return (
    <S.CommunityInfoCard>
      <S.CommunityInfoContainer>
        <Grid container justifyContent='space-between'>
          <S.CommunityInfoTitle container flexDirection='column' item md={7}>
            <p>{editedData?.plan_details?.name || 'Nome da comunidade'}</p>
            <p className='subTitle'>{editedData?.plan_details?.tagline || editedData?.plan_details?.group || 'Descrição da comunidade'}</p>
          </S.CommunityInfoTitle>
          <Grid display='flex' justifyContent='flex-end' alignItems='flex-end' item md={5} gap='10px'>
            {!isEditing && (
              <S.CommunityInfoEditButton onClick={handleEdit}>
                <img src={pencil} />
              </S.CommunityInfoEditButton>
            )}
          </Grid>
        </Grid>
      </S.CommunityInfoContainer>
      <S.CommunityInfoFormGrid container flexDirection='column'>
        {isEditing && (
          <>
            <Grid container>
              <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
                <p>Nome</p>
              </Grid>
              <S.CommunityInfoInputContainer item md={9} xl={9}>
                <S.CommunityInfoInputGrid>
                  <S.CommunityInfoInput
                    name='name'
                    id='name'
                    value={editedData?.plan_details?.name || ''}
                    onChange={handleInputChange}
                  />
                </S.CommunityInfoInputGrid>
              </S.CommunityInfoInputContainer>
            </Grid>
            <Grid container>
              <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
                <p>Descrição</p>
              </Grid>
              <S.CommunityInfoInputContainer item md={9} xl={9}>
                <S.CommunityInfoInputGrid>
                  <S.CommunityInfoInput
                    name='tagline'
                    id='tagline'
                    value={editedData?.plan_details?.tagline || ''}
                    onChange={handleInputChange}
                  />
                </S.CommunityInfoInputGrid>
              </S.CommunityInfoInputContainer>
            </Grid>
          </>
        )}
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Propósito</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                multiline
                name='purpose'
                id='purpose'
                value={editedData?.plan_details?.purpose || ''}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid>
        {(data?.plan_details?.specialInfo || isEditing) && (
          <Grid container>
            <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
              <p>Informações especiais</p>
            </Grid>
            <S.CommunityInfoInputContainer item md={9} xl={9}>
              <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
                <S.CommunityInfoInput
                  multiline
                  name='specialInfo'
                  id='specialInfo'
                  value={editedData?.plan_details?.specialInfo || ''}
                  onChange={handleInputChange}
                  InputProps={{
                    readOnly: !isEditing
                  }}
                />
              </S.CommunityInfoInputGrid>
            </S.CommunityInfoInputContainer>
          </Grid>
        )}
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Plataforma</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                name='platform'
                id='platform'
                value={Array.isArray(editedData?.plan_details?.platform) 
                  ? editedData.plan_details.platform.join(', ')
                  : (editedData?.plan_details?.platform || '')
                }
                onChange={(e) => handleArrayChange('platform', e.target.value)}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid>
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Assinatura</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                multiline
                placeholder='Assinatura'
                name='subscription'
                id='subscription'
                value={editedData?.plan_details?.subscription || ''}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid>
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Canais sugeridos</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            {isEditing ? (
              <S.CommunityInfoInputGrid>
                <S.CommunityInfoInput
                  name='channels'
                  id='channels'
                  value={editedData?.plan_details?.channels ? editedData.plan_details.channels.join(', ') : ''}
                  onChange={(e) => handleArrayChange('channels', e.target.value)}
                  placeholder="Canais separados por vírgula"
                />
              </S.CommunityInfoInputGrid>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {editedData?.plan_details?.channels && editedData.plan_details.channels.length > 0 ? (
                  editedData.plan_details.channels.map((channel, index) => (
                    <S.CommunityInfoChip
                      key={index}
                      label={channel}
                    />
                  ))
                ) : (
                  <>
                    <S.CommunityInfoChip label="Grupo Principal" />
                    <S.CommunityInfoChip label="Canal de Dúvidas" />
                    <S.CommunityInfoChip label="Canal de Compartilhamentos" />
                  </>
                )}
              </Box>
            )}
          </S.CommunityInfoInputContainer>
        </Grid>
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Hashtags</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            {isEditing ? (
              <S.CommunityInfoInputGrid>
                <S.CommunityInfoInput
                  name='hashtags'
                  id='hashtags'
                  value={editedData?.plan_details?.hashtags ? editedData.plan_details.hashtags.join(', ') : ''}
                  onChange={(e) => handleArrayChange('hashtags', e.target.value)}
                  placeholder="Hashtags separadas por vírgula"
                />
              </S.CommunityInfoInputGrid>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {editedData?.plan_details?.hashtags && editedData.plan_details.hashtags.length > 0 ? (
                  editedData.plan_details.hashtags.map((hashtag, index) => (
                    <S.CommunityInfoChip
                      key={index}
                      label={hashtag}
                    />
                  ))
                ) : (
                  <>
                    <S.CommunityInfoChip label="#TestesDeIA" />
                    <S.CommunityInfoChip label="#InteligenciaArtificial" />
                    <S.CommunityInfoChip label="#AprendizadoIA" />
                  </>
                )}
              </Box>
            )}
          </S.CommunityInfoInputContainer>
        </Grid>
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Frequência</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                placeholder='Frequência'
                name='creatorFrequency'
                id='frequency'
                value={editedData?.plan_details?.creatorFrequency || ''}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid>
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Mensagem de boas-vindas</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                multiline
                placeholder='Mensagem de boas-vindas'
                name='welcomeMessage'
                id='welcomeMessage'
                value={editedData?.plan_details?.welcomeMessage || ''}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid> 
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Regras da comunidade</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                multiline
                placeholder='Regras da comunidade (uma por linha)'
                name='rules'
                id='rules'
                value={Array.isArray(editedData?.plan_details?.rules) ? editedData.plan_details.rules.join('\n') : ''}
                onChange={(e) => handleRulesChange(e.target.value)}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid> 
        <Grid container>
          <Grid item md={2.5} xl={2.5} sx={{ display: 'flex', alignItems: 'center' }}>
            <p>Desafio de Engajamento</p>
          </Grid>
          <S.CommunityInfoInputContainer item md={9} xl={9}>
            <S.CommunityInfoInputGrid className={!isEditing ? 'readOnly' : ''}>
              <S.CommunityInfoInput
                placeholder='Desafio de Engajamento'
                name='engagementChallenge'
                multiline
                id='engagementChallenge'
                value={editedData?.plan_details?.engagementChallenge || ''}
                onChange={handleInputChange}
                InputProps={{
                  readOnly: !isEditing
                }}
              />
            </S.CommunityInfoInputGrid>
          </S.CommunityInfoInputContainer>
        </Grid> 
      </S.CommunityInfoFormGrid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: '10px' }}>
        {isEditing ? (
          <>
            <S.CommunityInfoDeleteButton 
              variant="contained"
              onClick={handleCancel}
              sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
            >
              Cancelar
            </S.CommunityInfoDeleteButton>
            <ButtonCustom 
              sx={{ width: '100px', fontSize: '14px' }}
              label="Salvar"
              onClick={handleSave}
            />
          </>
        ) : (
          <S.CommunityInfoDeleteButton 
            variant="contained"
            onClick={() => handleCommunityDelete(data)}
          >
            Apagar comunidade
          </S.CommunityInfoDeleteButton>
        )}
      </Box>
      <Modal 
        size="sm" 
        open={openConfirmDeletionModal} 
        onClose={() => setOpenConfirmDeletionModal(false)}
      >
        <Grid container flexDirection="column" gap="20px">
          <S.CommunityInfoModalTitle variant="h5">
            Confirmar exclusão
          </S.CommunityInfoModalTitle>
          <S.CommunityInfoModalText variant="body1">
            Deseja excluir este plano de comunidade? Essa ação é irreversível e também irá apagar o calendário de conteúdo, os roteiros gerados e as medalhas.
          </S.CommunityInfoModalText>
          <Grid container gap="15px" justifyContent="center">
            <Grid item lg={3}>
              <ButtonCustom
                label="Cancelar"
                onClick={() => cancelCommunityDelete()}
                sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
              />
            </Grid>
            <Grid item lg={3}>
              <ButtonCustom label="Confirmar" onClick={() => handleDeleteCommunity(data.id)} />
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    </S.CommunityInfoCard>
  );
};

export default CommunityInfo;